import { useCameraStore } from '@renderer/stores/cameraStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { unifiedCameraUtils } from './uniCamUtils'

/**
 * Session-start camera gate — shared by EVERY Welcome page (view, bahela,
 * bahelakotak via bahela, newspaper, bahela-quick).
 *
 * This is the exact gate view/Welcome.tsx has always run before starting a
 * capture session, extracted so the brand welcomes stop diverging: bahela's
 * Welcome used to navigate a guest straight to Filter with NO camera selected
 * or connected — the guest walked into a broken capture flow with no hint the
 * system was down (fatal for unmanned booths).
 *
 * Behavior:
 * - Active camera instance + responding            → true (proceed)
 * - Active instance but not responding             → CameraPrompt, false
 * - No instance, but a camera selected in history  → CameraPrompt, false
 *   (one tap reconnects; with camera_reconnect_v2 the tap is a silent re-bind)
 * - Nothing selected at all                        → SettingsModal, false
 *   (an operator must pick once; View's boot validation restores/auto-detects
 *   the selection on later boots, so this stays rare)
 *
 * The dispatched window events are handled by View.tsx, which renders the
 * CameraPrompt overlay for standard AND custom-path (bahela) shells alike.
 * Note: a camera PARKED by camera_rest reports healthy here by design — the
 * session's first capture auto-wakes it.
 */
export async function ensureCameraReadyForStart(): Promise<boolean> {
  const cameraStore = useCameraStore.getState()
  const { selectedCamera } = useSettingsStore.getState()
  const hasActiveCamera = !!(cameraStore.camera || cameraStore.webcam)

  if (!hasActiveCamera) {
    if (selectedCamera) {
      const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')
      if (!qualityResult.isResponding) {
        console.warn('[CAMERA-GATE] Camera selected but not responding, showing CameraPrompt')
        window.dispatchEvent(new CustomEvent('showCameraPrompt'))
        return false
      }
      return true
    }
    console.warn('[CAMERA-GATE] No camera selected for capture mode')
    window.dispatchEvent(new CustomEvent('showSettingsModal'))
    return false
  }

  const qualityResult = await unifiedCameraUtils.isDeviceResponding('primary')
  if (!qualityResult.isResponding) {
    console.warn('[CAMERA-GATE] Camera connected but not responding, showing CameraPrompt')
    window.dispatchEvent(new CustomEvent('showCameraPrompt'))
    return false
  }
  return true
}
